import { AdminList, AdminDataGrid, GridColumn } from './shared';
/**
 * Chat Conversation List Component
 * Displays paginated list of AI therapist chat conversations
 */

import {
  TextField,
  DateField,
  SearchInput,
  useRecordContext,
} from "react-admin";
import { Chip, Typography, Box } from "@mui/material";
import { semantic, brand } from "@rekindle/shared/theme";
import { ListActions, UserEmailField } from "./shared";

/**
 * Custom field to display message count with color coding
 */
function MessageCountField() {
  const record = useRecordContext();
  const count = record?.messageCount ?? 0;

  let color: string = semantic.neutral;
  if (count > 50) {
    color = brand.primary;
  } else if (count > 20) {
    color = semantic.success;
  } else if (count > 5) {
    color = semantic.info;
  }

  return (
    <Chip
      label={`${count} messages`}
      size="small"
      sx={{
        backgroundColor: color,
        color: "white",
      }}
    />
  );
}

/**
 * Custom field for activity status
 */
function ActivityField() {
  const record = useRecordContext();
  if (!record?.lastMessageAt) {
    return (
      <Typography variant="body2" color="textSecondary">
        No activity
      </Typography>
    );
  }

  const lastActivity = new Date(record.lastMessageAt);
  const now = new Date();
  const hoursDiff = Math.floor(
    (now.getTime() - lastActivity.getTime()) / (1000 * 60 * 60),
  );

  let status = "inactive";
  let color: string = semantic.neutral;

  if (hoursDiff < 1) {
    status = "Active now";
    color = semantic.success;
  } else if (hoursDiff < 24) {
    status = "Today";
    color = semantic.info;
  } else if (hoursDiff < 168) {
    status = "This week";
    color = semantic.warning;
  } else {
    status = "Inactive";
    color = semantic.neutral;
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: color,
        }}
      />
      <Typography variant="body2">{status}</Typography>
    </Box>
  );
}

/**
 * Filters for chat conversation list
 */
const conversationFilters = [
  <SearchInput key="q" source="q" alwaysOn placeholder="Search by user..." />,
];

export function ChatConversationList() {
  return (
    <AdminList
      filters={conversationFilters}
      actions={<ListActions />}
      sort={{ field: "createdAt", order: "DESC" }}
      perPage={25}
    >
      <AdminDataGrid bulkActionButtons={false} rowClick="show" omit={['id']}>
        <TextField source="id" label="Conversation ID" />
        <GridColumn label="User" source="userEmail"><UserEmailField /></GridColumn>
        <GridColumn label="Messages" source="messageCount"><MessageCountField /></GridColumn>
        <GridColumn label="Activity" sortable={false}><ActivityField /></GridColumn>
        <DateField source="createdAt" label="Started" showTime />
        <DateField source="lastMessageAt" label="Last Activity" showTime sortable={false} />
      </AdminDataGrid>
    </AdminList>
  );
}
